import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CSEForm from "@/components/CSEForm";
import { CSE_OFFERS, CSE_FAQ, CSE_CITIES, CSE_HUB } from "@/data/cse-pages";
import { overline, btnOutline } from "@/components/kit";
import { track } from "@/lib/track";
import { ArrowRight, ArrowUpRight, Check, MapPin, Truck, Users, Handshake } from "lucide-react";
import { CONTACT } from "@/data/site";

const ARGUMENTS = [
  { icon: Users, title: "Un seul interlocuteur", text: "Un chef de projet organise votre événement CSE du premier appel au jour J : animations, restauration, technique, personnel." },
  { icon: Handshake, title: "Pensé pour les élus bénévoles", text: "Vous validez les grandes étapes, nous gérons tout le reste. Votre temps est précieux — nous le respectons." },
  { icon: Truck, title: "Tout est apporté sur place", text: "Dans vos locaux, sur votre site ou dans une salle privatisée : matériel, animations et logistique sont coordonnés par nos soins." },
];

export default function CSEPage() {
  const { ville } = useParams();
  const city = ville ? CSE_CITIES[ville] : null;
  if (ville && !city) return <Navigate to="/evenements-cse" replace />;

  const isHub = !city;
  const title = isHub ? CSE_HUB.seoTitle : city.seoTitle;
  const description = isHub ? CSE_HUB.seoDescription : city.seoDescription;
  const h1 = isHub ? CSE_HUB.h1 : city.h1;
  const path = isHub ? "/evenements-cse" : `/evenements-cse/${city.slug}`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
      { "@type": "ListItem", position: 2, name: "Événements CSE", item: "https://desir-events.com/evenements-cse" },
      ...(city ? [{ "@type": "ListItem", position: 3, name: city.name, item: `https://desir-events.com${path}` }] : []),
    ],
  };

  return (
    <>
      <Seo title={title} description={description} path={path} />
      <Helmet><script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script></Helmet>

      <PageHero
        eyebrow={city ? `Événements CSE · ${city.name}` : "Offre dédiée aux CSE"}
        title={<>{h1.split("CSE")[0]}<span className="italic text-lime-dim">CSE{city ? ` à ${city.name}` : ""}</span></>}
      />

      {/* Positionnement */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="cse-intro">
        <div className="max-w-3xl">
          <Reveal>
            <p className="text-lg leading-relaxed text-ink/70">
              {city ? city.intro : "Comités sociaux et économiques : Désir Events organise vos arbres de Noël, journées du personnel, soirées et sorties collectives — un seul interlocuteur, du choix des animations à la coordination le jour J. Notre terrain de jeu principal : Lyon, Grenoble, Valence et toute la région Rhône-Alpes ; nous intervenons aussi partout en France sur étude."}
            </p>
          </Reveal>
          <Reveal delay={1}>
            <p className="mt-5 rounded-2xl border border-lime-dim/40 bg-lime/10 p-5 text-sm leading-relaxed text-ink/70" data-testid="cse-asc-note">
              Bon à savoir : pour les entreprises de 50 salariés et plus, ces événements relèvent généralement du budget des activités sociales et culturelles (ASC), distinct du budget de fonctionnement. Nos formules sont <strong>sur devis</strong>, calibrées à votre enveloppe et à vos effectifs — jamais de prix plaqué.
            </p>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {ARGUMENTS.map((a, i) => (
            <Reveal key={a.title} delay={i}>
              <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7">
                <a.icon size={20} className="text-lime-dim" />
                <h2 className="mt-4 font-serif text-xl text-ink">{a.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Mise en avant arbre de Noël (pages qui le demandent, ex : Lyon) */}
      {city?.noelHighlight && (
        <section className="border-t border-ink/10 bg-white" data-testid="cse-noel-highlight">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
            <Reveal><span className={overline}>Le rendez-vous de décembre</span></Reveal>
            <Reveal delay={1}><h2 className="mt-4 max-w-3xl font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">{city.noelHighlight.title}</h2></Reveal>
            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {city.noelHighlight.items.map((it) => (
                <div key={it} className="flex items-start gap-3 rounded-xl border border-ink/10 bg-mist px-5 py-4">
                  <Check size={15} className="mt-0.5 shrink-0 text-lime-dim" />
                  <p className="text-sm font-medium text-ink">{it}</p>
                </div>
              ))}
            </div>
            <Reveal delay={2}><p className="mt-6 max-w-2xl text-sm text-ink/50">Formule sur devis, adaptée à votre budget d'activités sociales et au nombre d'enfants. En décembre, réservez idéalement dès septembre-octobre.</p></Reveal>
          </div>
        </section>
      )}

      {/* Les 4 offres */}
      <section className="border-t border-ink/10 bg-mist" data-testid="cse-offers">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Nos formats CSE</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Quatre événements phares, chacun sur devis</h2></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {CSE_OFFERS.map((o, i) => (
              <Reveal key={o.id} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-8" data-testid={`cse-offer-${o.id}`} id={o.id}>
                  <h3 className="font-serif text-2xl text-ink">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{o.text}</p>
                  <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ink/40">Inclus dans la formule</p>
                  <ul className="mt-3 space-y-2">
                    {o.inclus.map((inc) => (
                      <li key={inc} className="flex items-start gap-2.5 text-sm text-ink/70">
                        <Check size={14} className="mt-0.5 shrink-0 text-lime-dim" />{inc}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-6 max-w-2xl text-sm text-ink/50">
              En transverse sur chaque format : bar mobile, animations et personnel événementiel de notre réseau interne. Chaque formule est chiffrée sur devis, poste par poste.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Organisation locale (pages ville) */}
      {city && (
        <section className="border-t border-ink/10 bg-white" data-testid="cse-local">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
            <div className="grid gap-12 md:grid-cols-2">
              <div>
                <Reveal><span className={overline}>Lieux & formats à {city.name}</span></Reveal>
                <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Des sites adaptés à votre CSE</h2></Reveal>
                <div className="mt-8 space-y-3">
                  {city.lieux.map((l) => (
                    <div key={l.name} className="flex items-start gap-3 rounded-xl border border-ink/10 bg-mist px-5 py-4">
                      <MapPin size={15} className="mt-0.5 shrink-0 text-lime-dim" />
                      <div>
                        <p className="font-medium text-ink">{l.name}</p>
                        <p className="text-sm text-ink/55">{l.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Reveal><span className={overline}>Organisation & déplacement</span></Reveal>
                <Reveal delay={1}>
                  <div className="mt-4 rounded-2xl border border-ink/10 bg-mist p-8">
                    <Truck size={20} className="text-lime-dim" />
                    <p className="mt-4 text-base leading-relaxed text-ink/70">{city.logistique}</p>
                    <p className="mt-4 text-sm text-ink/50">Installation, démontage et coordination compris — vous récupérez un site propre.</p>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Formulaire dédié */}
      <section className="border-t border-ink/10 bg-ink text-white" data-testid="cse-form-section">
        <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-16 md:grid-cols-2 md:px-10 md:py-24">
          <div>
            <Reveal><span className={`${overline} !text-lime`}>Votre événement CSE</span></Reveal>
            <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">Une proposition en 2 minutes.</h2></Reveal>
            <Reveal delay={2}>
              <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
                Indiquez le type d'événement, vos effectifs et votre enveloppe : un chef de projet rappelle votre contact sous 24 h ouvrées avec une première piste adaptée à votre budget ASC.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={`${btnOutline} mt-8 !border-white/25 !text-white`} data-testid="cse-call-btn">
                Une question ? {CONTACT.phoneMobile}
              </a>
            </Reveal>
          </div>
          <Reveal delay={1}>
            <CSEForm city={city?.name || ""} cityCode={city?.code || ""} testid={city ? `cse-form-${city.slug}` : "cse-form"} />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white" data-testid="cse-faq">
        <div className="mx-auto max-w-[900px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes des élus</span></Reveal>
          <div className="mt-8 space-y-4">
            {CSE_FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i}>
                <details className="group rounded-2xl border border-ink/10 bg-mist p-6 open:bg-white">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-ink">
                    {f.q}
                    <ArrowRight size={16} className="shrink-0 text-lime-dim transition-transform duration-300 group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{f.r}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="border-t border-ink/10 bg-mist" data-testid="cse-related">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>{city ? "Pour aller plus loin" : "Nos pages CSE locales"}</span></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {city ? (
              <>
                {city.related.map((r) => (
                  <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`cse-related-${r.to.replace(/\//g, "")}`}>
                    {r.label} <ArrowUpRight size={14} className="text-lime-dim" />
                  </Link>
                ))}
              </>
            ) : (
              <>
                {Object.values(CSE_CITIES).map((c) => (
                  <Link key={c.slug} to={`/evenements-cse/${c.slug}`} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`cse-city-${c.slug}`}>
                    Événements CSE à {c.name} <ArrowUpRight size={14} className="text-lime-dim" />
                  </Link>
                ))}
                <Link to="/agence-evenementielle-entreprise-lyon" className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink">
                  Notre agence événementielle à Lyon <ArrowUpRight size={14} className="text-lime-dim" />
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
